import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft, Save, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export function CadastrarProjetoSocialPage() {
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState({
    nome: '',
    categoria: '',
    descricao: '',
    orcamento: '',
    dataInicio: new Date().toISOString().split('T')[0],
    dataFim: '',
    responsavel: '',
    beneficiarios: '',
    objetivos: '',
    observacoes: ''
  })

  const [isLoading, setIsLoading] = useState(false)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.nome || !formData.categoria || !formData.orcamento || !formData.dataInicio) {
      alert('Por favor, preencha todos os campos obrigatórios.')
      return
    }

    if (formData.dataFim && new Date(formData.dataFim) <= new Date(formData.dataInicio)) {
      alert('A data de fim deve ser posterior à data de início.')
      return
    }

    setIsLoading(true)

    try {
      const novoProjeto = {
        nome: formData.nome,
        categoria: formData.categoria,
        descricao: formData.descricao,
        orcamento: parseFloat(formData.orcamento),
        gastoAtual: 0,
        dataInicio: formData.dataInicio,
        dataFim: formData.dataFim || undefined,
        responsavel: formData.responsavel,
        beneficiarios: formData.beneficiarios ? parseInt(formData.beneficiarios) : 0,
        objetivos: formData.objetivos,
        observacoes: formData.observacoes || undefined,
        status: 'ativo' as const
      }

      // Aqui você salvaria no store
      console.log('Novo projeto social:', novoProjeto)
      
      // Redirecionar para a listagem
      navigate('/financas/administracao/projetos-sociais')
    } catch (error) {
      console.error('Erro ao cadastrar projeto social:', error)
      alert('Erro ao cadastrar projeto social. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/financas/administracao/projetos-sociais')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Cadastrar Novo Projeto Social</h1>
          <p className="text-muted-foreground">
            Registre um novo projeto social ou assistencial da igreja
          </p>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <CardHeader>
          <CardTitle>Informações do Projeto</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Projeto *</Label>
                <Input
                  id="nome"
                  placeholder="Ex: Cesta Básica Solidária"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria *</Label>
                <Select 
                  value={formData.categoria} 
                  onValueChange={(value) => handleInputChange('categoria', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Assistência Social">Assistência Social</SelectItem>
                    <SelectItem value="Educação">Educação</SelectItem>
                    <SelectItem value="Saúde">Saúde</SelectItem>
                    <SelectItem value="Evangelização">Evangelização</SelectItem>
                    <SelectItem value="Capacitação">Capacitação</SelectItem>
                    <SelectItem value="Outros">Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="orcamento">Orçamento Total *</Label>
                <Input
                  id="orcamento"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={formData.orcamento}
                  onChange={(e) => handleInputChange('orcamento', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="beneficiarios">Número de Beneficiários</Label>
                <Input
                  id="beneficiarios"
                  type="number"
                  min="0"
                  placeholder="Ex: 150"
                  value={formData.beneficiarios}
                  onChange={(e) => handleInputChange('beneficiarios', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dataInicio">Data de Início *</Label>
                <Input
                  id="dataInicio"
                  type="date"
                  value={formData.dataInicio}
                  onChange={(e) => handleInputChange('dataInicio', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dataFim">Data de Fim</Label>
                <Input
                  id="dataFim"
                  type="date"
                  value={formData.dataFim}
                  onChange={(e) => handleInputChange('dataFim', e.target.value)}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="responsavel">Responsável</Label>
                <Input
                  id="responsavel"
                  placeholder="Nome do responsável pelo projeto"
                  value={formData.responsavel}
                  onChange={(e) => handleInputChange('responsavel', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                placeholder="Descreva brevemente o projeto"
                value={formData.descricao}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="objetivos">Objetivos</Label>
              <Textarea
                id="objetivos"
                placeholder="Descreva os objetivos e metas do projeto"
                value={formData.objetivos}
                onChange={(e) => handleInputChange('objetivos', e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                placeholder="Observações adicionais (opcional)"
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                rows={3}
              />
            </div>

            {/* Botões */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isLoading ? 'Salvando...' : 'Salvar Projeto'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}